import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Container,
  IconButton,
  Tooltip,
  Divider,
  Alert,
  Snackbar,
  LinearProgress,
  CircularProgress,
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ScienceIcon from '@mui/icons-material/Science';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTabs } from '../contexts/TabsContext';
import TestFilters from '../components/testScenarios/TestFilters';
import TestScenariosTable from '../components/testScenarios/TestScenariosTable';
import { getAllScenariosForDisplay } from '../data/pricingScenarios';
import { getAllScenariosWithResultsAsync } from '../data/pricingScenarioValidation';
import { buildScenarioTabSettings } from '../data/pricingScenarioApplyConfig';

const DEFAULT_FILTERS = {
  search: '',
  category: [],
  tier: [],
  region: [],
  status: [],
};

export default function TestScenariosPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createTab } = useTabs();

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  // Load scenarios without validation initially
  const [allScenarios, setAllScenarios] = useState(() => getAllScenariosForDisplay());
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [hasRunTests, setHasRunTests] = useState(false);

  // Run API validation (triggered by button)
  const runApiValidation = useCallback(async () => {
    setIsLoading(true);
    setLoadingProgress(0);
    setHasRunTests(true);

    const displayScenarios = getAllScenariosForDisplay();

    try {
      // Fetch real validation from backend API with progress updates
      const validatedScenarios = await getAllScenariosWithResultsAsync((processed, total, partialResults) => {
        const progress = Math.round((processed / total) * 100);
        setLoadingProgress(progress);

        // Update UI incrementally with validated scenarios
        const remaining = displayScenarios.slice(processed);
        setAllScenarios([...partialResults, ...remaining]);
      });
      setAllScenarios(validatedScenarios);
      setLoadingProgress(100);

      // Show success message
      const passed = validatedScenarios.filter(s => s.validation?.passed).length;
      const failed = validatedScenarios.filter(s => s.validation && !s.validation.passed && !s.validation.skipped).length;
      setSnackbar({
        open: true,
        message: t('testScenarios.validationComplete', 'Validation complete: {{passed}} passed, {{failed}} failed', { passed, failed }),
        severity: failed > 0 ? 'warning' : 'success',
      });
    } catch (error) {
      console.error('Failed to validate scenarios against API:', error);
      setSnackbar({
        open: true,
        message: t('testScenarios.apiError', 'Failed to validate against backend API.'),
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  // Apply filters
  const filteredScenarios = useMemo(() => {
    return allScenarios.filter(scenario => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        if (!scenario.description.toLowerCase().includes(searchLower)) {
          return false;
        }
      }

      // Category filter (multi-select) — match if ANY selected category
      // appears in productsList (by id) OR matches scenario.category
      if (filters.category.length > 0) {
        const productIds = scenario.productsList?.map(p => p.id) || [];
        const matches = filters.category.some(
          cat => productIds.includes(cat) || scenario.category === cat
        );
        if (!matches) return false;
      }

      // Tier filter (multi-select)
      if (filters.tier.length > 0 && !filters.tier.includes(scenario.tier)) {
        return false;
      }

      // Region filter (multi-select)
      if (filters.region.length > 0 && !filters.region.includes(scenario.region)) {
        return false;
      }

      // Status filter (multi-select)
      if (filters.status.length > 0) {
        const isPassed = scenario.validation?.passed ?? true;
        const scenarioStatus = isPassed ? 'passed' : 'failed';
        if (!filters.status.includes(scenarioStatus)) return false;
      }

      return true;
    });
  }, [allScenarios, filters]);

  // Calculate stats
  const stats = useMemo(() => {
    const total = filteredScenarios.length;
    const passed = filteredScenarios.filter(s => s.validation?.passed ?? true).length;
    const failed = total - passed;
    return { total, passed, failed };
  }, [filteredScenarios]);

  // Handle Apply to Calculator
  const handleApplyScenario = useCallback(async (scenario) => {
    try {
      if (!scenario.calculatorConfig) {
        setSnackbar({
          open: true,
          message: t('testScenarios.noConfig', 'No calculator config available for this scenario'),
          severity: 'warning',
        });
        return;
      }

      const tabSettings = buildScenarioTabSettings(scenario);

      // Create new tab with scenario name and settings
      const tabName = `Test: ${scenario.description.substring(0, 35)}`;
      const newTabId = await createTab({
        name: tabName,
        settings: tabSettings,
      });

      if (newTabId) {
        // Open calculator in new tab/window
        window.open('/', '_blank');

        setSnackbar({
          open: true,
          message: t('testScenarios.tabCreated', 'Tab "{{name}}" created! Opening calculator in new window...', { name: tabName }),
          severity: 'success',
        });
      }
    } catch (error) {
      console.error('Error applying scenario:', error);
      setSnackbar({
        open: true,
        message: t('testScenarios.applyError', 'Error creating tab'),
        severity: 'error',
      });
    }
  }, [createTab, t]);

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f7f9f8', py: 3 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            bgcolor: 'white',
            borderRadius: 2,
            border: '1px solid rgba(0,0,0,0.12)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Tooltip title={t('testScenarios.backToCalculator', 'Back to Calculator')}>
              <IconButton onClick={() => navigate('/')} size="small">
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <ScienceIcon color="primary" sx={{ fontSize: 32 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {t('testScenarios.title', 'Test Scenarios Dashboard')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('testScenarios.subtitle', 'Pricing scenarios for validation against backend API')}
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
              onClick={runApiValidation}
              disabled={isLoading}
              sx={{ minWidth: 180 }}
            >
              {isLoading
                ? t('testScenarios.running', 'Running... {{progress}}%', { progress: loadingProgress })
                : hasRunTests
                  ? t('testScenarios.rerun', 'Re-run Tests')
                  : t('testScenarios.runTests', 'Run API Tests')
              }
            </Button>
          </Box>

          {/* Loading progress */}
          {isLoading && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress variant="determinate" value={loadingProgress} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {t('testScenarios.validating', 'Validating {{count}} scenarios against backend API...', { count: allScenarios.length })}
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Info alert */}
          <Alert severity={hasRunTests ? 'success' : 'info'} sx={{ mb: 2 }}>
            {hasRunTests
              ? t(
                  'testScenarios.infoAfterRun',
                  'Tests validated against backend API. Click "Apply" to create a calculator tab with scenario settings.'
                )
              : t(
                  'testScenarios.info',
                  'Click "Run API Tests" to validate all 120 scenarios against the backend pricing engine. Click "Apply" to create a calculator tab.'
                )
            }
          </Alert>

          {/* Filters */}
          <TestFilters
            filters={filters}
            onFilterChange={setFilters}
            stats={stats}
          />
        </Paper>

        {/* Table */}
        <Paper
          elevation={0}
          sx={{
            bgcolor: 'white',
            borderRadius: 2,
            border: '1px solid rgba(0,0,0,0.12)',
            overflow: 'hidden',
          }}
        >
          <TestScenariosTable
            scenarios={filteredScenarios}
            onApplyScenario={handleApplyScenario}
          />
        </Paper>
      </Container>

      {/* Snackbar notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
